import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Heart, Sparkles, History, ArrowRight } from "lucide-react";
import {
  WELLBEING_DESTINATIONS,
  INTEGRATE_SIGNATURE_DESTINATIONS,
  ALL_WELLBEING_DESTINATIONS,
  type WellbeingDestination,
} from "@/data/wellbeing-destinations";
import {
  readWellbeingRecents,
  recordWellbeingVisit,
} from "@/lib/wellbeing-recents";


export const Route = createFileRoute("/holistic-wellbeing")({
  head: () => ({
    meta: [
      { title: "Wellbeing Hub — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your gentle wellbeing dashboard — aromatherapy, nature connection, forest bathing, gardening, creative reflection and relaxation practices.",
      },
      {
        property: "og:title",
        content: "Wellbeing Hub — Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Daily featured practice, recently viewed and favourites — a calm, interactive wellbeing dashboard.",
      },
      {
        property: "og:url",
        content: "https://recoverytrauma.com/holistic-wellbeing",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://recoverytrauma.com/holistic-wellbeing",
      },
    ],
  }),
  component: HolisticWellbeingPage,
});

const FAVS_KEY = "wb-favs";

function readList(key: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeList(key: string, value: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function HolisticWellbeingPage() {
  const [recent, setRecent] = useState<string[]>([]);
  const [favs, setFavs] = useState<string[]>([]);

  useEffect(() => {
    setRecent(readWellbeingRecents());
    setFavs(readList(FAVS_KEY));
  }, []);

  const featured = useMemo<WellbeingDestination>(() => {
    const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
    return ALL_WELLBEING_DESTINATIONS[
      day % ALL_WELLBEING_DESTINATIONS.length
    ];
  }, []);

  const recentDestinations = useMemo(
    () =>
      recent
        .map((slug) => ALL_WELLBEING_DESTINATIONS.find((d) => d.slug === slug))
        .filter((d): d is WellbeingDestination => Boolean(d)),
    [recent],
  );

  const favDestinations = useMemo(
    () =>
      favs
        .map((slug) => ALL_WELLBEING_DESTINATIONS.find((d) => d.slug === slug))
        .filter((d): d is WellbeingDestination => Boolean(d)),
    [favs],
  );


  function recordVisit(slug: string) {
    setRecent(recordWellbeingVisit(slug));
  }

  function toggleFav(slug: string) {
    const next = favs.includes(slug)
      ? favs.filter((s) => s !== slug)
      : [slug, ...favs];
    setFavs(next);
    writeList(FAVS_KEY, next);
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      {/* Slim hero */}
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <span aria-hidden>🌿</span> Wellbeing Hub
        </div>
        <h1 className="mt-3 font-display text-2xl tracking-tight text-foreground sm:text-3xl md:text-4xl">
          Gentle wellbeing, your way
        </h1>
        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Pick a practice that fits this moment — no order, no goal.
        </p>
      </header>

      {/* Featured practice today */}
      <FeaturedCard destination={featured} onOpen={recordVisit} />

      {/* Destinations */}
      <section className="mt-10" aria-labelledby="destinations-heading">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2
            id="destinations-heading"
            className="font-display text-lg text-foreground sm:text-xl"
          >
            Explore
          </h2>
          <span className="text-xs text-muted-foreground">
            {WELLBEING_DESTINATIONS.length} practices
          </span>
        </div>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {WELLBEING_DESTINATIONS.map((d) => (
            <li key={d.slug}>
              <DestinationCard
                destination={d}
                isFav={favs.includes(d.slug)}
                onOpen={() => recordVisit(d.slug)}
                onToggleFav={() => toggleFav(d.slug)}
              />
            </li>
          ))}
        </ul>
      </section>

      {/* Signature Recovery Trauma™ topics */}
      <section className="mt-10" aria-labelledby="signature-heading">
        <div className="mb-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <div className="min-w-0">
            <h2
              id="signature-heading"
              className="font-display text-lg text-foreground sm:text-xl"
            >
              Recovery in everyday life
            </h2>
            <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
              Signature Recovery Trauma™ topics — drawing on DBT, Internal
              Family Systems and shadow work.
            </p>
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">
            {INTEGRATE_SIGNATURE_DESTINATIONS.length} topics
          </span>
        </div>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {INTEGRATE_SIGNATURE_DESTINATIONS.map((d) => (
            <li key={d.slug}>
              <DestinationCard
                destination={d}
                isFav={favs.includes(d.slug)}
                onOpen={() => recordVisit(d.slug)}
                onToggleFav={() => toggleFav(d.slug)}
              />
            </li>
          ))}
        </ul>
      </section>


      {/* Recently viewed */}
      {recentDestinations.length > 0 && (
        <section className="mt-10" aria-labelledby="recent-heading">
          <div className="mb-3 flex items-center gap-2">
            <History className="h-4 w-4 text-[var(--gold-deep)]" />
            <h2
              id="recent-heading"
              className="font-display text-base text-foreground sm:text-lg"
            >
              Recently viewed
            </h2>
          </div>
          <ul className="flex flex-wrap gap-2">
            {recentDestinations.map((d) => (
              <li key={d.slug}>
                <Link
                  to={d.path as "/"}
                  onClick={() => recordVisit(d.slug)}
                  className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/30 bg-card px-3 py-1.5 text-sm text-foreground transition hover:border-gold/55 hover:bg-cream/40"
                >
                  <span aria-hidden>{d.emoji}</span> {d.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Favourites */}
      {favDestinations.length > 0 && (
        <section className="mt-10" aria-labelledby="favs-heading">
          <div className="mb-3 flex items-center gap-2">
            <Heart className="h-4 w-4 fill-rose-400 text-rose-400" />
            <h2
              id="favs-heading"
              className="font-display text-base text-foreground sm:text-lg"
            >
              Favourites
            </h2>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {favDestinations.map((d) => (
              <li key={d.slug}>
                <DestinationCard
                  destination={d}
                  isFav
                  compact
                  onOpen={() => recordVisit(d.slug)}
                  onToggleFav={() => toggleFav(d.slug)}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/* ---------- Components ---------- */

function FeaturedCard({
  destination,
  onOpen,
}: {
  destination: WellbeingDestination;
  onOpen: (slug: string) => void;
}) {
  const d = destination;
  return (
    <Link
      to={d.path as "/"}
      onClick={() => onOpen(d.slug)}
      className="group relative flex items-stretch gap-4 overflow-hidden rounded-[2rem] border border-gold-light/60 bg-gradient-to-br from-[oklch(0.97_0.025_85)] via-card to-[oklch(0.93_0.05_82)] p-5 shadow-[0_24px_60px_-32px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)] transition-all duration-300 hover:-translate-y-1 dark:border-gold-light/40 dark:from-[oklch(0.30_0.015_78)] dark:via-card dark:to-[oklch(0.34_0.04_80)] sm:p-7"
    >
      <div
        aria-hidden
        className="relative flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl text-4xl ring-1 ring-gold-light/50 sm:h-28 sm:w-28 sm:text-6xl"
        style={{
          background: `radial-gradient(ellipse at center, color-mix(in oklab, ${d.tint} 30%, var(--cream)) 0%, color-mix(in oklab, ${d.tint} 12%, var(--cream)) 75%, transparent 100%)`,
        }}
      >
        <span>{d.emoji}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <Sparkles className="h-3 w-3" /> Featured today
        </div>
        <h2 className="mt-1 font-display text-xl leading-tight text-foreground sm:text-2xl">
          {d.title}
        </h2>
        <p className="mt-1 text-sm text-foreground/75 sm:text-base">
          {d.description}
        </p>
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--gold-deep)]">
          Open practice
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function DestinationCard({
  destination,
  isFav,
  compact = false,
  onOpen,
  onToggleFav,
}: {
  destination: WellbeingDestination;
  isFav: boolean;
  compact?: boolean;
  onOpen: () => void;
  onToggleFav: () => void;
}) {
  const d = destination;
  return (
    <div className="group relative">
      <Link
        to={d.path as "/"}
        onClick={onOpen}
        className="flex h-full items-start gap-3 rounded-2xl border border-gold/25 bg-card p-4 transition hover:-translate-y-0.5 hover:border-gold/55 hover:bg-cream/40 hover:shadow-[var(--shadow-gold)]"
      >
        <div
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-2xl ring-1 ring-gold-light/40"
          style={{
            background: `radial-gradient(ellipse at center, color-mix(in oklab, ${d.tint} 28%, var(--cream)) 0%, color-mix(in oklab, ${d.tint} 10%, var(--cream)) 80%, transparent 100%)`,
          }}
        >
          <span>{d.emoji}</span>
        </div>
        <div className="min-w-0 flex-1 pe-7">
          <div className="font-display text-base text-foreground">
            {d.title}
          </div>
          {!compact && (
            <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
              {d.description}
            </p>
          )}
        </div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleFav();
        }}
        aria-label={isFav ? `Remove ${d.title} from favourites` : `Add ${d.title} to favourites`}
        aria-pressed={isFav}
        className="absolute end-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-foreground/50 transition hover:bg-cream/60 hover:text-rose-500"
      >
        <Heart
          className={
            isFav
              ? "h-4 w-4 fill-rose-400 text-rose-400"
              : "h-4 w-4"
          }
        />
      </button>
    </div>
  );
}
